
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../dashboard/components/Navbar';
import BottomNav from '../dashboard/components/BottomNav';

export default function Reports() {
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState<'daily' | 'weekly' | 'monthly'>('monthly');
  const [selectedMonth, setSelectedMonth] = useState('2024-01');

  // بيانات تجريبية للتقارير
  const reportData = {
    daily: {
      totalTransfers: 15,
      totalAmount: 12500,
      totalCommission: 312.5,
      successRate: 93.3
    },
    weekly: {
      totalTransfers: 89,
      totalAmount: 67800,
      totalCommission: 1695,
      successRate: 95.5
    },
    monthly: {
      totalTransfers: 342,
      totalAmount: 285600,
      totalCommission: 7140,
      successRate: 94.7
    }
  };

  const monthlyBreakdown = [
    { month: 'يناير 2024', transfers: 342, amount: 285600, commission: 7140 },
    { month: 'ديسمبر 2023', transfers: 298, amount: 245800, commission: 6145 },
    { month: 'نوفمبر 2023', transfers: 267, amount: 198900, commission: 4972 },
    { month: 'أكتوبر 2023', transfers: 289, amount: 234500, commission: 5862 },
    { month: 'سبتمبر 2023', transfers: 312, amount: 267800, commission: 6695 },
    { month: 'أغسطس 2023', transfers: 278, amount: 189600, commission: 4740 }
  ];

  const topCountries = [
    { country: 'الإمارات العربية المتحدة', transfers: 89, percentage: 26 },
    { country: 'المملكة العربية السعودية', transfers: 76, percentage: 22 },
    { country: 'الكويت', transfers: 54, percentage: 16 },
    { country: 'قطر', transfers: 43, percentage: 13 },
    { country: 'البحرين', transfers: 38, percentage: 11 },
    { country: 'عمان', transfers: 42, percentage: 12 }
  ];

  const currentData = reportData[selectedPeriod];

  const getPeriodText = (period: string) => {
    switch (period) {
      case 'daily':
        return 'يومي';
      case 'weekly':
        return 'أسبوعي';
      case 'monthly':
        return 'شهري';
      default:
        return 'شهري';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      <Navbar />
      
      <div className="pt-20 pb-20 px-4">
        <div className="max-w-md mx-auto">
          {/* العنوان */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">التقارير المالية</h1>
            <p className="text-gray-600 mt-2">تحليل شامل للعمليات والأرباح</p>
          </div>

          {/* اختيار الفترة */}
          <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
            <h3 className="font-semibold text-gray-800 mb-3">فترة التقرير</h3>
            <div className="grid grid-cols-3 gap-2">
              {(['daily', 'weekly', 'monthly'] as const).map((period) => (
                <button
                  key={period}
                  onClick={() => setSelectedPeriod(period)}
                  className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                    selectedPeriod === period 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {getPeriodText(period)}
                </button>
              ))}
            </div>
          </div>

          {/* الإحصائيات الرئيسية */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">إجمالي التحويلات</p>
                  <p className="text-2xl font-bold text-blue-600">{currentData.totalTransfers}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <i className="ri-exchange-line text-blue-600 text-xl"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">إجمالي المبلغ</p>
                  <p className="text-xl font-bold text-green-600">${currentData.totalAmount.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <i className="ri-money-dollar-circle-line text-green-600 text-xl"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">إجمالي العمولات</p>
                  <p className="text-xl font-bold text-purple-600">${currentData.totalCommission.toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <i className="ri-percent-line text-purple-600 text-xl"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">معدل النجاح</p>
                  <p className="text-xl font-bold text-orange-600">{currentData.successRate}%</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <i className="ri-check-line text-orange-600 text-xl"></i>
                </div>
              </div>
            </div>
          </div>

          {/* أهم الدول */}
          <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
              <i className="ri-global-line ml-2 text-blue-600"></i>
              أهم الدول المستهدفة
            </h3>
            <div className="space-y-3">
              {topCountries.map((country, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center ml-3">
                      <span className="text-xs font-bold text-blue-600">{index + 1}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">{country.country}</p>
                      <p className="text-xs text-gray-600">{country.transfers} تحويل</p>
                    </div>
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-blue-600">{country.percentage}%</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* التقرير الشهري */}
          <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
              <i className="ri-calendar-line ml-2 text-green-600"></i>
              التقرير الشهري
            </h3>
            <div className="space-y-3">
              {monthlyBreakdown.map((month, index) => (
                <div key={index} className="border border-gray-100 rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-gray-800">{month.month}</h4>
                    <span className="text-sm text-gray-600">{month.transfers} تحويل</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">إجمالي المبلغ:</span>
                      <p className="font-medium text-green-600">${month.amount.toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">العمولات:</span>
                      <p className="font-medium text-purple-600">${month.commission.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* رسم بياني تجريبي */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
              <i className="ri-bar-chart-line ml-2 text-orange-600"></i>
              الرسم البياني الشهري
            </h3>
            <div className="h-48 bg-gray-50 rounded-lg flex items-center justify-center">
              <img 
                src="https://public.readdy.ai/ai/img_res/a88f7b74ae98f005395559dd8dc1918f.jpg"
                alt="الرسم البياني الشهري"
                className="w-full h-full object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
